import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Save, ShieldAlert, ShieldCheck, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";

const AutoRules = () => {
  const { user, api } = useAuth();
  const [rules, setRules] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role === "agent") {
      api.get("/admin/auto-rules").then(({ data }) => setRules(data)).catch(console.error);
    }
  }, [user, api]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-cf-black text-cf-muted">Loading…</div>;
  if (user === false) return <Navigate to="/agent/login" replace />;
  if (user.role !== "agent") return <Navigate to="/merchant/dashboard" replace />;
  if (!rules) return <div className="min-h-screen flex items-center justify-center bg-cf-black text-cf-muted">Loading rules…</div>;

  const upd = (k, v) => { setSaved(false); setRules({ ...rules, [k]: v }); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/auto-rules", rules);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#05070A", color: "#F8F9FA" }} data-testid="auto-rules-page">
      <header className="border-b border-white/5" style={{ background: "rgba(10,14,23,0.4)" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <CFLogo variant="paper" testId="auto-rules-logo" />
            <div className="hidden md:flex items-center gap-2 pl-5 border-l border-white/10">
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-cf-gold font-semibold flex items-center gap-1">
                <Settings size={11} /> Auto-decision Rules
              </span>
            </div>
          </div>
          <Link to="/agent/dashboard" className="text-xs text-cf-muted hover:text-cf-gold-light cf-link-underline flex items-center gap-1">
            <ArrowLeft size={12} /> Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <h1 className="font-serif text-3xl tracking-tight">Auto-decision rules</h1>
          <p className="text-cf-muted text-sm mt-2">
            When a merchant applies, these rules run automatically and can pre-set the lead status.
            Files outside the rules still appear normally and wait for manual review.
          </p>
        </div>

        <div className="border border-white/5 rounded-lg p-5" style={{ background: "rgba(10,14,23,0.4)" }}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="rules-enabled" checked={rules.enabled} onChange={(e) => upd("enabled", e.target.checked)} />
            <span className="text-sm font-semibold">Enable auto-decision rules</span>
          </label>
          <p className="text-cf-muted text-xs mt-2">When off, every new application stays in <em>Pending</em> for manual review.</p>
        </div>

        <div className="border border-rose-500/20 bg-rose-500/5 rounded-lg p-5">
          <div className="flex items-center gap-2 text-rose-300 font-semibold text-sm mb-3">
            <ShieldAlert size={14} /> Auto-decline (when revenue/TIB falls below)
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <NumberField label="Monthly revenue below ($)" value={rules.auto_decline_below_monthly_revenue} onChange={(v) => upd("auto_decline_below_monthly_revenue", v)} testId="ad-revenue" />
            <NumberField label="Time in business below (months)" value={rules.auto_decline_below_tib_months} onChange={(v) => upd("auto_decline_below_tib_months", v)} testId="ad-tib" />
          </div>
          <p className="text-cf-muted text-xs mt-3">Tip: set to 0 to disable an individual threshold. Default: $10K + 6 months.</p>
        </div>

        <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-lg p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
              <ShieldCheck size={14} /> Auto-approve (instant approvals — use carefully)
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" data-testid="aa-enabled" checked={rules.auto_approve_enabled} onChange={(e) => upd("auto_approve_enabled", e.target.checked)} />
              <span>Enable</span>
            </label>
          </div>
          <div className={`grid sm:grid-cols-3 gap-4 ${rules.auto_approve_enabled ? "" : "opacity-40 pointer-events-none"}`}>
            <NumberField label="Min monthly revenue ($)" value={rules.auto_approve_min_monthly_revenue} onChange={(v) => upd("auto_approve_min_monthly_revenue", v)} testId="aa-revenue" />
            <NumberField label="Min time in business (months)" value={rules.auto_approve_min_tib_months} onChange={(v) => upd("auto_approve_min_tib_months", v)} testId="aa-tib" />
            <NumberField label="Max amount ($)" value={rules.auto_approve_max_amount} onChange={(v) => upd("auto_approve_max_amount", v)} testId="aa-amount" />
          </div>
          <p className="text-cf-muted text-xs mt-3">
            Files matching ALL three criteria + a requested amount ≤ the max will auto-flip to <em>Approved</em>.
            Off by default — only enable when you have proven thresholds.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="save-rules" className="cf-btn-ghost"
                  style={{ fontSize: "0.75rem", background: "#B68A35", color: "#fff", borderColor: "#B68A35" }}>
            <Save size={13} /> {saving ? "Saving…" : "Save rules"}
          </button>
          {saved && <span className="text-emerald-300 text-xs">✓ Saved</span>}
        </div>
      </main>
    </div>
  );
};

const NumberField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted block mb-2">{label}</label>
    <input
      data-testid={testId}
      type="number" min="0" inputMode="numeric"
      value={value} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      className="cf-input"
    />
  </div>
);

export default AutoRules;
